import type {
  IOrderDetailGetResponse,
  IOrderGetListResponse,
  IWalletCreateRequest,
  IWalletCreateResponse,
  IWalletGetListResponse,
  IWalletDeleteRequest,
  IWalletExtendRequest,
  IWalletUpdateNameRequest,
  IWalletSetDefaultRequest,
  IWalletUpdateUseForTypeRequest,
  IRenewalScheduleGetListResponse,
  IRenewalProgramGetListResponse,
  IRenewalKitGetListResponse,
  IUserVisibilityFieldsGetResponse,
  IUserVisibilitySettingsGetResponse,
  IUserVisibilitySettingsUpdateRequest
} from '@/types/OrderTypes.js'
import { ApiPost, ApiGet, ApiPostWithLargeIntegerSupport } from './Common.ts'
import { GetOrderEntryConnectorUrl } from '@/stores/SettingsStore.ts'

export async function OrderDetailGet(aoeOrderIdToken: string): Promise<IOrderDetailGetResponse> {
  const url = await GetOrderEntryConnectorUrl('OrderDetailGet')
  return ApiPost(url, { aoeOrderIdToken })
}

export async function OrderDetailGetListByCustomer(): Promise<IOrderGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('OrderDetailGetListByCustomer')
  return ApiGet(url)
}

export async function OrderDetailGetListByPayee(filter : unknown): Promise<IOrderGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('OrderDetailGetListByPayee')
  return ApiGet(url, filter)
}

export async function LiveOrderDetailGetList(filter : unknown): Promise<IOrderGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('LiveOrderDetailGetList')
  return ApiGet(url, filter)
}

export async function PaidOrderDetailGetList(filter : unknown): Promise<IOrderGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('PaidOrderDetailGetList')
  return ApiGet(url, filter)
}

// Wallet API Methods
export async function WalletCreate(request: IWalletCreateRequest): Promise<IWalletCreateResponse> {
  const url = await GetOrderEntryConnectorUrl('WalletCreate')
  return ApiPost(url, request)
}

export async function WalletGetList(): Promise<IWalletGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('WalletGetList')
  return ApiGet(url)
}

export async function WalletDelete(request: IWalletDeleteRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('WalletDelete')
  return ApiPost(url, request)
}


export async function WalletExtend(request: IWalletExtendRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('WalletExtend')
  return ApiPost(url, request)
}

/**
 * Gets wallet list with proper GUID handling to avoid JavaScript rounding issues
 * Uses ApiPostWithLargeIntegerSupport to retrieve correct customerCardGuid and cusWalletGuid values
 * @returns Promise with wallet information containing proper GUID strings
 */
export async function WalletGetListWithGuidSupport(): Promise<IWalletGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('WalletGetList')
  return ApiPostWithLargeIntegerSupport(url, {})
}

/**
 * Updates the name/card name of a wallet entry
 * @param request Object containing cusWalletGuid and cardName
 * @returns Promise with the response
 */
export async function WalletUpdateName(request: IWalletUpdateNameRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('WalletUpdateName')
  return ApiPost(url, request)
}

/**
 * Sets a wallet card as the default payment method
 * @param request Object containing cusWalletGuid and isDefault flag
 * @returns Promise with the response
 */
export async function WalletSetDefault(request: IWalletSetDefaultRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('WalletSetDefault')
  return ApiPost(url, request)
}

/**
 * Updates the use for type of a wallet card (e.g., for renewal)
 * @param request Object containing cusWalletGuid and useForType
 * @returns Promise with the response
 */
export async function WalletUpdateUseForType(request: IWalletUpdateUseForTypeRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('WalletUpdateUseForType')
  return ApiPost(url, request)
}

/**
 * Gets the list of renewal schedules
 * @returns Promise with the list of renewal schedules
 */
export async function PwsGenRenewalScheduleGetList(renewalId: number): Promise<IRenewalScheduleGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('PwsGenRenewalScheduleGetList')
  return ApiPost(url, { renewalId })
}

/**
 * Gets the list of renewal programs for a given renewal ID
 * @param renewalId The renewal ID to get programs for
 * @returns Promise with the list of renewal programs
 */
export async function PwsGenRenewalProgramGetList(renewalId: number): Promise<IRenewalProgramGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('PwsGenRenewalProgramGetList')
  return ApiPost(url, { renewalId })
}

/**
 * Gets the list of renewal kits for a given renewal ID and program ID
 * @param renewalId The renewal ID to get kits for
 * @param renewalProgramId The renewal program ID to get kits for
 * @returns Promise with the list of renewal kits
 */
export async function PwsGenRenewalKitGetList(renewalId: number, renewalProgramId: string): Promise<IRenewalKitGetListResponse> {
  const url = await GetOrderEntryConnectorUrl('PwsGenRenewalKitGetList')
  return ApiPost(url, { renewalId, renewalProgramId })
}

/**
 * Gets the list of user visibility fields for generating checkboxes
 * Uses ApiPostWithLargeIntegerSupport to handle large UtlFieldValue integers without precision loss
 * @returns Promise with the list of visibility fields
 */
export async function UserVisibilityFieldsGet(): Promise<IUserVisibilityFieldsGetResponse> {
  const url = await GetOrderEntryConnectorUrl('UserVisibilityFieldsGet')
  return ApiPostWithLargeIntegerSupport(url, {})
}

/**
 * Gets the current user visibility settings
 * @returns Promise with the current visibility settings
 */
export async function UserVisibilitySettingsGet(): Promise<IUserVisibilitySettingsGetResponse> {
  const url = await GetOrderEntryConnectorUrl('UserVisibilitySettingsGet')
  return ApiPost(url, {})
}

/**
 * Updates user visibility settings with checkbox values
 * @param request Object containing genPlanGuid, beeNumber, and settings array
 * @returns Promise with the response
 */
export async function UserVisibilitySettingsUpdate(request: IUserVisibilitySettingsUpdateRequest): Promise<{ responseStatus: { errorCode: string, message: string } }> {
  const url = await GetOrderEntryConnectorUrl('UserVisibilitySettingsUpdate')
  return ApiPost(url, request)
}
